// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Anchor, Box, Group, Loader, Modal, Paper, Stack, Table, Text, TextInput, Title } from '@mantine/core';
import type { DocumentReference, MedicationRequest } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconDownload, IconSearch } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { type JSX, useEffect, useMemo, useState } from 'react';

function formatDate(value: string | undefined): string {
  if (!value) {
    return '—';
  }
  return dayjs(value).format('MM/DD/YYYY');
}

function getModality(request: MedicationRequest): string {
  return (
    request.medicationCodeableConcept?.text ??
    request.medicationCodeableConcept?.coding?.[0]?.display ??
    request.medicationCodeableConcept?.coding?.[0]?.code ??
    '—'
  );
}

function getCptCode(request: MedicationRequest): string {
  return request.medicationCodeableConcept?.coding?.[0]?.code ?? '—';
}

function getCondition(request: MedicationRequest): string {
  return request.reasonCode?.[0]?.coding?.[0]?.code ?? request.reasonCode?.[0]?.text ?? '—';
}

function getExpires(request: MedicationRequest): string {
  return formatDate(request.dispenseRequest?.validityPeriod?.end);
}

function getLastFilled(request: MedicationRequest): string {
  return formatDate(request.dispenseRequest?.validityPeriod?.start);
}

function getDocument(request: MedicationRequest, documents: DocumentReference[]): DocumentReference | undefined {
  const expectedReference = `MedicationRequest/${request.id}`;
  return documents.find((document) =>
    document.context?.related?.some(
      (reference) => reference.reference === expectedReference || reference.reference?.endsWith(`/${expectedReference}`)
    )
  );
}

export interface PrescriptionsSectionProps {
  prescriptions: MedicationRequest[];
  prescriptionDocuments: DocumentReference[];
  loading: boolean;
}

export function PrescriptionsSection({
  prescriptions,
  prescriptionDocuments,
  loading,
}: PrescriptionsSectionProps): JSX.Element {
  const medplum = useMedplum();
  const [query, setQuery] = useState('');
  const [documentUrl, setDocumentUrl] = useState<string>();
  const [documentTitle, setDocumentTitle] = useState('Prescription');

  useEffect(() => {
    return () => {
      if (documentUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(documentUrl);
      }
    };
  }, [documentUrl]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return prescriptions;
    }
    return prescriptions.filter((r) =>
      `${getModality(r)} ${getCptCode(r)} ${getCondition(r)} ${r.requester?.display ?? ''}`.toLowerCase().includes(q)
    );
  }, [prescriptions, query]);

  const handleViewScript = async (request: MedicationRequest): Promise<void> => {
    const document = getDocument(request, prescriptionDocuments);
    const rawUrl = document?.content?.[0]?.attachment?.url;
    if (!rawUrl) {
      return;
    }
    const url = rawUrl.startsWith('Binary/') ? URL.createObjectURL(await medplum.download(rawUrl)) : rawUrl;
    setDocumentTitle(document.content?.[0]?.attachment?.title ?? 'Prescription');
    setDocumentUrl(url);
  };

  return (
    <Paper withBorder radius="md" shadow="sm" p="xl">
      <Group justify="space-between" mb="md" wrap="wrap">
        <Title order={2}>Prescriptions</Title>
        <TextInput
          placeholder="Search…"
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          leftSection={<IconSearch size={14} />}
          size="sm"
          w={240}
          aria-label="Search prescriptions"
        />
      </Group>

      {loading ? (
        <Stack align="center" py="md">
          <Loader size="sm" />
        </Stack>
      ) : (
        <Box style={{ overflowX: 'auto' }}>
          <Table highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Modality</Table.Th>
                <Table.Th>Code(s)</Table.Th>
                <Table.Th>Condition</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Ordered by</Table.Th>
                <Table.Th>Order Date</Table.Th>
                <Table.Th>Last Filled</Table.Th>
                <Table.Th>Expires</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={9}>
                    <Text ta="center" c="dimmed" py="md">
                      No prescriptions on file.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                filtered.map((rx) => (
                  <Table.Tr key={rx.id}>
                    <Table.Td>{getModality(rx)}</Table.Td>
                    <Table.Td>{getCptCode(rx)}</Table.Td>
                    <Table.Td>{getCondition(rx)}</Table.Td>
                    <Table.Td>{rx.status ?? '—'}</Table.Td>
                    <Table.Td>{rx.requester?.display ?? '—'}</Table.Td>
                    <Table.Td>{formatDate(rx.authoredOn)}</Table.Td>
                    <Table.Td>{getLastFilled(rx)}</Table.Td>
                    <Table.Td>{getExpires(rx)}</Table.Td>
                    <Table.Td>
                      {getDocument(rx, prescriptionDocuments) ? (
                        <Anchor component="button" type="button" size="sm" onClick={() => handleViewScript(rx)}>
                          View Script
                        </Anchor>
                      ) : (
                        <Text size="sm" c="dimmed">
                          Unavailable
                        </Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </Box>
      )}
      <Modal opened={Boolean(documentUrl)} onClose={() => setDocumentUrl(undefined)} title={documentTitle} size="xl">
        {documentUrl && (
          <Stack>
            <iframe title={documentTitle} src={documentUrl} style={{ width: '100%', height: '70vh', border: 0 }} />
            <Anchor href={documentUrl} download={`${documentTitle}.pdf`}>
              <IconDownload size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Download PDF
            </Anchor>
          </Stack>
        )}
      </Modal>
    </Paper>
  );
}
